// eslint-disable-next-line import/no-unresolved
import storyContentStyles from '!css-loader?{"url":false,"modules":false,"exportType":"string"}!sass-loader!@opengov/stories-shared/styles/story-modules.css';
import { CircularProgress, Typography, Box, Paper } from '@mui/material';
import React, { useState, useEffect, useRef } from 'react';
import { ISetupPdfMessages } from '../../../../../types/setupPDFTypes';
import { styles } from './StoryPreview.styles';
import { useFetchStoryHtmlContent, useUpdateSectionContent, fetchSectionContent } from '../hooks';
import { useProgressTrackerSettingsContext } from '../../../StoriesSettings/ProgressTrackerSettings/context/useProgressTrackerSettingsContext';
import Frame, { FrameContextComponent } from 'react-frame-component';
import { applyCustomCssToScope, getStoryCustomCssScopeId } from '@opengov/stories-shared/utils';
import {
  HIGHLIGHT_ACTIVE_ELEMENT_CLASSNAME,
  PUBLICATION_CUSTOM_CSS_ID_ATTR,
  STORY_CUSTOM_CSS_ID_ATTR
} from '../../../../utils/stylesheetsUtils';
import { storyContentIdPrefix, pageBreakClassName, HEADER_FOOTER_ZOOM_FACTOR, SECTION_ZOOM_FACTOR, SECTION_PADDING_FACTOR } from './StoryPreview.constants';


export interface IStoryPreviewProps {
  storyId: string;
  entityId: number;
  messages: ISetupPdfMessages;
  activeElement?: HTMLElement | null;
  iframeDocumentRef: React.MutableRefObject<Document | null>;
  setActiveElement?: (activeElement: HTMLElement | null) => void;
}

export const StoryPreview: React.FC<IStoryPreviewProps> = ({
  storyId,
  messages,
  entityId,
  iframeDocumentRef,
  activeElement
}) => {
  const [iframeDoc, setIframeDoc] = useState<Document | null>(null);
  const { progressTrackerSettings } = useProgressTrackerSettingsContext();
  const {
    data: storyHtmlContent,
    isLoading: isLoadingStoryHtmlContent,
    isError
  } = useFetchStoryHtmlContent({ storyId, entityId, progressTrackerSettings });

  // Mutation for updating section content (page breaks)
  const updateSectionMutation = useUpdateSectionContent({ storyId });

  // Store mutation in ref so click handlers can access latest version
  const updateSectionRef = useRef(updateSectionMutation.mutateAsync);
  updateSectionRef.current = updateSectionMutation.mutateAsync;

  // Reset iframe document ref when component mounts or storyId changes
  // This ensures proper re-initialization when switching between preview/edit modes
  useEffect(() => {
    iframeDocumentRef.current = null;
    setIframeDoc(null);
  }, [storyId, iframeDocumentRef]);

  // Toggle page break class on section elements
  const toggleSectionPageBreakClass = (htmlString: string): string => {
    const parser = new DOMParser();
    const doc = parser.parseFromString(htmlString, 'text/html');
    const section = doc.querySelector('section');
    if (section) {
      section.classList.toggle(pageBreakClassName);
    }
    return doc.body.innerHTML;
  };

  /**
   * Adds toolbar buttons to each <section>
   */
  const addSectionButtons = (doc: Document) => {
    const sections = doc.querySelectorAll('section');

    sections.forEach((section) => {
      if (section.querySelector('.section-toolbar')) return; // skip duplicates

      const btn = doc.createElement('button');
      const hasBreak = section.classList.contains(pageBreakClassName);

      btn.className = 'section-toolbar ' + (hasBreak ? 'remove' : 'add');

      // Wrap label in span so border styling works
      const label = doc.createElement('span');
      label.className = 'setupPdfPageBreakButton';
      label.textContent = hasBreak ? messages.removePageBreak : messages.addPageBreak;
      btn.appendChild(label);

      btn.addEventListener('click', async (e) => {
        e.stopPropagation();
        e.preventDefault();

        const setupPdfPageBreakButton = btn.querySelector('.setupPdfPageBreakButton');
        if (!setupPdfPageBreakButton) return;

        const sectionIdWithPrefix = section.id;
        if (sectionIdWithPrefix.startsWith(storyContentIdPrefix) && storyId) {
          try {
            // Fetch current section content using helper function (no Redux effects)
            const sectionContentData = await fetchSectionContent({
              storyId,
              contentSubstring: `%${sectionIdWithPrefix}%`
            });
            const updatedSectionHtml = toggleSectionPageBreakClass(sectionContentData.content);

            // Use mutation to update section content (handles cache invalidation automatically)
            await updateSectionRef.current({
              sectionId: sectionContentData.id,
              updatedContent: updatedSectionHtml
            });
          } catch (error) {
            console.error('Failed to update page break:', error);
            return; // Don't update UI if API call failed
          }
        }

        if (section.classList.contains(pageBreakClassName)) {
          section.classList.remove(pageBreakClassName);
          btn.className = 'section-toolbar add';
          setupPdfPageBreakButton.textContent = messages.addPageBreak;
        } else {
          section.classList.add(pageBreakClassName);
          btn.className = 'section-toolbar remove';
          setupPdfPageBreakButton.textContent = messages.removePageBreak;
        }
      });

      section.insertBefore(btn, section.firstChild);
    });
  };

  const handleElementClick = (event: Event) => {
    event.preventDefault();
    event.stopPropagation();
  };

  const subscribeIframeEvents = (ref: FrameContextComponent | null) => {
    if (!ref) return;
    const iframeNode: HTMLIFrameElement | null = ref.node as HTMLIFrameElement | null;
    const iframeDocument = iframeNode?.contentWindow?.document;
    if (iframeDocument && !iframeDocumentRef.current) {
      iframeDocumentRef.current = iframeDocument;
      setIframeDoc(iframeDocument);
    }
  };

  useEffect(() => {
    if (activeElement) activeElement.classList.add(HIGHLIGHT_ACTIVE_ELEMENT_CLASSNAME);
    return () => {
      if (activeElement) activeElement.classList.remove(HIGHLIGHT_ACTIVE_ELEMENT_CLASSNAME);
    };
  }, [activeElement]);

  useEffect(() => {
    if (!iframeDoc) return;
    iframeDoc.body.addEventListener('click', handleElementClick);

    return () => {
      iframeDoc.body.removeEventListener('click', handleElementClick);
    };
  }, [iframeDoc]);
  if (!storyId) {
    return (
      <Box data-test="storySetupPDFEmptyPreview" sx={styles.container}>
        <Typography color="text.secondary">No story selected</Typography>
      </Box>
    );
  }
  if (isLoadingStoryHtmlContent) {
    return (
      <Box data-test="setupPDFStoryPreviewLoadingSpinner" sx={styles.container}>
        <CircularProgress />
        <Typography>{messages.loading}</Typography>
      </Box>
    );
  }

  // Error is handled by useFetchStoryHtmlContent hook which shows toast notification
  if (isError) {
    return <Box data-test="setupPDFStoryPreviewError" sx={styles.container}></Box>;
  }

  const scopeId = getStoryCustomCssScopeId();
  const scopeSelector = `#${scopeId}`;
  return (
    <Paper data-test="setupPDFStoryPreview" sx={styles.container}>
      <Frame
        style={{
          width: '100%',
          height: '100%',
          border: 'none'
        }}
        ref={(ref: FrameContextComponent) => {
          setTimeout(() => subscribeIframeEvents(ref), 0);
        }}
        head={
          <>
            <style>{storyContentStyles}</style>
            <style data-id={PUBLICATION_CUSTOM_CSS_ID_ATTR}>
              {applyCustomCssToScope(storyHtmlContent?.publicationCustomCss || '', scopeSelector)}
            </style>
            <style data-id={STORY_CUSTOM_CSS_ID_ATTR}>
              {applyCustomCssToScope(storyHtmlContent?.storyCustomCss || '', scopeSelector)}
            </style>
          </>
        }
        initialContent={storyHtmlContent?.html || ''}
        onLoad={() => {
          if (!iframeDoc) return;

          if (!iframeDoc.getElementById('section-toolbar-styles')) {
            const style = iframeDoc.createElement('style');
            style.id = 'section-toolbar-styles';
            style.innerHTML = `
              .section-toolbar {
                display: flex;
                align-items: center;
                justify-content: center;
                width: 100%;
                margin: 8px 0;
                background: transparent;
                border: none;
                cursor: pointer;
                font-size: 14px;
                position: absolute;
                top: 0px;
                left: 0px;
                z-index: 100;
              }
              .section-toolbar::before,
              .section-toolbar::after {
                content: "";
                flex: 1;
                border-bottom: 1px dashed currentColor;
                margin: 0 8px;
              }
              .section-toolbar.add { color: #3E5DE6; }
              .section-toolbar.remove { color: #ff0000; }

              .setupPdfPageBreakButton {
                border: 1px solid currentColor;
                border-radius: 4px;
                padding: 2px 8px;
                background: #fff;
              }

              section { 
                position: relative;
                zoom: ${SECTION_ZOOM_FACTOR};
                padding-left: ${SECTION_PADDING_FACTOR} !important;
                padding-right: ${SECTION_PADDING_FACTOR} !important;
              }

              header { zoom: ${HEADER_FOOTER_ZOOM_FACTOR}; }
              footer { zoom: ${HEADER_FOOTER_ZOOM_FACTOR}; }
              .powered-by { display: none; }
              @media print {
                .section-toolbar { display: none !important; }
                section.${pageBreakClassName} {
                  break-before: page;
                  page-break-before: always;
                }
              }
            `;
            iframeDoc.head.appendChild(style);
          }
          // set timeout to add section buttons after the iframe is loaded
          setTimeout(() => addSectionButtons(iframeDoc), 50);
        }}
      />
    </Paper>
  );
};

export default StoryPreview;
